/** @jsxImportSource @emotion/react */
import classNames from 'classnames';
import { Formik, FormikConfig, FormikProps } from 'formik';
import _ from 'lodash';
import {
  BOARD_RESOLUTION_LIST,
  BoardResolutionKey,
  DEFAULT_BOARD_RESOLUTION,
} from 'modules/manager/domain/board-resolutions';
import { modalButton, modalError, modalInputStyle } from 'modules/template-editor/ui/components/elements/modals/styles';
import React from 'react';
import UDFormInput from 'ud-form/components/input';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import { Button } from 'ud-ui/components/button/styles';
import Modal from 'ud-ui/components/modal';
import * as yup from 'yup';

export type ResolutionOption = SelectOption<BoardResolutionKey>;

export type MediaManagerBoardFormValues = {
  name: string;
  resolution: ResolutionOption;
};

type Props = FormikConfig<MediaManagerBoardFormValues> & {
  existingNames: string[];
  isLoading: boolean;
  submitLabel?: string;
};

export const RESOLUTION_OPTIONS: ResolutionOption[] = BOARD_RESOLUTION_LIST.map((r) => ({
  value: r.key,
  label: r.label,
}));

export const DEFAULT_RESOLUTION_OPTION: ResolutionOption = RESOLUTION_OPTIONS.find(
  (o) => o.value === DEFAULT_BOARD_RESOLUTION.key,
)!;

export const getResolutionOption = (key: BoardResolutionKey): ResolutionOption =>
  RESOLUTION_OPTIONS.find((o) => o.value === key) ?? DEFAULT_RESOLUTION_OPTION;

const validator = (existingNames: string[]) =>
  yup.object({
    name: yup
      .string()
      .transform((val) => (typeof val === 'string' ? val.trim() : val))
      .test('media-manager-board-name', 'Invalid name', function (value) {
        if (!value || value.length <= 0) {
          return this.createError({ message: 'Название табло не может быть пустым' });
        }
        if (existingNames.includes(value)) {
          return this.createError({ message: 'Такое название уже существует, придумайте новое' });
        }
        return true;
      }),
    resolution: yup
      .object({
        value: yup.string().required(),
        label: yup.string().required(),
      })
      .required('Выберите разрешение'),
  });

export const DEFAULT_BOARD_FORM_VALUES: MediaManagerBoardFormValues = {
  name: '',
  resolution: DEFAULT_RESOLUTION_OPTION,
};

function MediaManagerBoardForm(props: Props) {
  const { existingNames, isLoading, submitLabel = 'Создать', ...rest } = props;

  const getErrors = (formProps: FormikProps<MediaManagerBoardFormValues>): string | undefined => {
    const error = _.find(
      formProps.errors,
      (err, key) => !!err && !!formProps.touched[key as keyof MediaManagerBoardFormValues],
    );
    if (!error) return undefined;
    return typeof error === 'string' ? error : Object.values(error).find((v): v is string => typeof v === 'string');
  };

  return (
    <Formik validateOnChange validationSchema={validator(existingNames)} {...rest}>
      {(formProps) => (
        <form onSubmit={formProps.handleSubmit} className="w-[450px]">
          <Modal.Body className="flex flex-col gap-5">
            <UDFormInput
              readOnly={isLoading}
              onChangeCapture={() => {
                formProps.values.name && !formProps.touched.name && formProps.setFieldTouched('name');
              }}
              name="name"
              autoFocus
              css={modalInputStyle}
              placeholder="Название табло"
            />
            <UDFormSelect
              name="resolution"
              label="Разрешение"
              placeholder="Выберите разрешение"
              options={RESOLUTION_OPTIONS}
            />
          </Modal.Body>
          <Modal.Footer justify="start">
            <Button
              type="submit"
              className={classNames('flex-center', isLoading && 'loading')}
              disabled={!formProps.isValid || formProps.values.name.trim() === '' || isLoading}
              css={modalButton}
            >
              {submitLabel}
            </Button>
            <span css={modalError}>{getErrors(formProps)}</span>
          </Modal.Footer>
        </form>
      )}
    </Formik>
  );
}

export default MediaManagerBoardForm;
